import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    linkedSignal,
    type OnInit,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { form, FormField, maxLength } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { NgIcon } from '@ng-icons/core';
import { toKebabCase } from '@quipex/shared/utils';
import {
    combineLatestWith,
    distinctUntilChanged,
    filter,
    map,
    tap,
} from 'rxjs';
import {
    type ButtonSize,
    QpxButton,
    QpxFormFieldDirectives,
    QpxIcon,
} from '../../../directives';
import { KebabCasePipe } from '../../../pipes';
import { QpxFormFieldComponents } from '../../form-field';
import { FILTER_LAYOUT_CONTEXT } from '../filter-layout';
import type {
    AppliedFilterOptionId,
    Filter,
    FilterOption,
    FilterOptionId,
} from './types';

/**
 * Type for initial filter options that can be either name strings, `FilterOptionIds` or partial objects with name/id of `FilterOption`
 */
type InitialAppliedOption<U extends FilterOptionId = FilterOptionId> =
    | string
    | U
    | Partial<FilterOption<U>>;

/**
 * Type guard to check if value is a partial filter option with id and/or name
 * @param value - Value to check
 * @returns true if value is an object with id or name properties
 */
function isPartialFilterOption<U extends FilterOptionId = FilterOptionId>(
    value: unknown
): value is Partial<FilterOption<U>> {
    return (
        typeof value === 'object' &&
        value !== null &&
        ('id' in value || 'name' in value)
    );
}

@Component({
    selector: 'qpx-filter-item',
    imports: [
        NgTemplateOutlet,
        FormField,
        MatButtonModule,
        QpxButton,
        MatFormFieldModule,
        NgIcon,
        QpxIcon,
        MatSelectModule,
        KebabCasePipe,
        QpxFormFieldDirectives,
        QpxFormFieldComponents,
        MatInputModule,
        MatMenuModule,
        MatCheckboxModule,
    ],
    templateUrl: './filter-item.html',
    styleUrls: ['./filter-item.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QpxFilterItem<
    T extends string = string,
    U extends FilterOptionId = FilterOptionId,
> implements OnInit {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Optional filter layout context.
     * Used to detect container layout for responsive behavior.
     */
    private readonly filterLayout = inject(FILTER_LAYOUT_CONTEXT, {
        optional: true,
    });
    //#endregion

    //#region Inputs
    /** filter name and options */
    public readonly filter = input.required<Filter<T, U>>();

    /** Displays a count of selected items in the trigger button (aka input) */
    public readonly counterWithPlaceholder = input<boolean>(false);

    /** Size applied to the trigger button */
    public readonly size = input<ButtonSize>('md');

    /** Displays options only without mat-menu */
    public readonly expandedOptionsWithSave = input<boolean>(false);

    /**
     * Displays a header row in the dropdown
     * which contains selected items count and clear filters button
     */
    public readonly showHeader = input<boolean>(true);

    /** CSS classes applied to the trigger button (aka input) */
    public readonly inputClasses = input<string[]>([]);
    protected readonly allInputClasses = computed(() => {
        return [
            ...this.inputClasses(),
            'filter-form-field',
            'trigger',
            `${toKebabCase(this.filter().name)}-filter`,
        ].join(' ');
    });

    public readonly panelWidth = input<'auto' | 'match'>('match');
    private readonly panelWidth$ = toObservable(this.panelWidth);

    /** CSS classes applied to the dropdown panel */
    public readonly panelClasses = input<string, string[]>('filter-menu', {
        transform: (value) => {
            return value.join(' ');
        },
    });
    protected readonly allPanelClasses = toSignal(
        toObservable(this.panelClasses).pipe(
            combineLatestWith(this.panelWidth$),
            map(([panel, width]) => {
                const auto =
                    width === 'auto' && panel.includes('dropdown-auto-width');
                return [panel, auto].filter(Boolean).join(' ');
            })
        ),
        { initialValue: 'filter-menu' }
    );

    /** Display content in the trigger button (aka input) */
    public readonly placeholder = input('Select');

    /** When `true`, return "all" option id if applied via `appliedChange` */
    public readonly returnAllOption = input(false);

    /** When `true`, return "none" option id if applied via `appliedChange` */
    public readonly returnNoneOption = input(true);

    /** When `true`, allows searching of filter items */
    public readonly enableSearch = input(false);

    /** Placeholder text for the conditionally-rendered search input */
    public readonly searchPlaceholder = input<string>('Search');

    /**
     * Selected options will automatically be applied on closing of the dropdown.
     * When enabled the footer will not be visible.
     */
    public readonly applyOnClose = input(false);

    /** When `true`, the "none" option is treated as a special case (like "all"), otherwise it's treated as a regular option. Defaults to `false`. */
    public readonly isNoneOptionSpecial = input(false);

    /** When `true`, the filter is disabled and not interactive */
    public readonly disabled = input(false);

    /** the "all" option */
    public readonly allOption = input<FilterOption<U> | null>(null);

    /** the "none" option */
    public readonly noneOption = input<FilterOption<U> | null>(null);

    /** When `true`, single select mode is activated */
    public readonly allowSingleSelectionOnly = input<boolean>(false);

    /**
     * Input property that accepts an array of
     * filter names or ids or partial filter options
     * to restore previously selected filters.
     *
     * @example
     * ```typescript
     * // In parent component
     * initialAppliedOptions = ['Building A', 'Building B'];
     * OR
     * initialAppliedOptions = [id(A), id(B)];
     * OR
     * initialAppliedOptions = [{ id: id(A) }, { id: id(B) }];
     * OR
     * initialAppliedOptions = [{name: 'Building A' }, { name: 'Building B' }];
     *
     * // In template
     * <qpx-filter-item [initialAppliedOptions]="initialAppliedOptions" />
     * ```
     */
    public readonly initialAppliedOptions = input<
        readonly InitialAppliedOption[] | null
    >(null);
    private readonly initialAppliedOptions$ = toObservable(
        this.initialAppliedOptions
    );
    // #endregion

    // #region Outputs
    /**
     * Event emitter that fires when filters are applied.
     * Emits an array of applied filter ids.
     *
     * @event
     * @template U The type of the filter model
     */
    public readonly appliedChange = output<AppliedFilterOptionId<U>[]>();
    // #endregion

    // #region Template references
    protected readonly menuTriggerRef = viewChild<unknown, MatMenuTrigger>(
        MatMenuTrigger,
        {
            read: MatMenuTrigger,
        }
    );
    protected readonly menuTriggerEl = viewChild<
        unknown,
        ElementRef<HTMLButtonElement>
    >(MatMenuTrigger, {
        read: ElementRef,
    });
    // #endregion

    // #region State
    protected readonly selectedOptionIds = signal<Set<U>>(new Set());
    protected readonly appliedAndSelectedOptionIds = signal<Set<U>>(new Set());

    protected readonly options = computed(() => this.filter().options);

    protected readonly filteredOptions = linkedSignal({
        source: () => ({
            filter: this.filter,
            allOption: this.allOption,
            noneOption: this.noneOption,
            search: this.search,
        }),
        computation: ({ filter, allOption, noneOption, search }) => {
            const allId = allOption()?.id;
            const noneId = noneOption()?.id;
            const searchValue = search();
            const isNoneSpecial = this.isNoneOptionSpecial();
            const noSpecialValue = (id: U) => {
                // Normalize IDs for comparison to handle string vs number mismatches
                const normalizedId = String(id);
                const normalizedAllId =
                    allId !== undefined ? String(allId) : undefined;
                const normalizedNoneId =
                    noneId !== undefined ? String(noneId) : undefined;
                return (
                    normalizedId !== normalizedAllId &&
                    (!isNoneSpecial || normalizedId !== normalizedNoneId)
                );
            };
            let filtered = filter().options.filter((option) =>
                noSpecialValue(option.id)
            );

            // Apply search filtering if search term exists
            if (searchValue && searchValue.length > 0) {
                filtered = filtered.filter((option) =>
                    option.name
                        .toLowerCase()
                        .includes(searchValue.toLowerCase())
                );
            }
            return filtered;
        },
    });

    protected readonly search = signal('');
    protected readonly searchField = form(this.search, (path) => {
        maxLength(path, 50);
    });

    /** Displays count + placeholder OR joined values of applied items */
    protected readonly triggerDisplayValue = computed(() => {
        const appliedIds = this.appliedAndSelectedOptionIds();

        if (!appliedIds.size || this.counterWithPlaceholder()) {
            return this.placeholder();
        }

        const allOption = untracked(this.allOption);
        const noneOption = untracked(this.noneOption);
        const isNoneSpecial = untracked(this.isNoneOptionSpecial);
        const options = [...this.filter().options];

        if (allOption !== null) {
            options.push(allOption);
        }

        if (noneOption !== null) {
            options.push(noneOption);
        }

        const normalisedAllId =
            allOption !== null ? String(allOption.id) : undefined;
        const normalisedNoneId =
            noneOption !== null ? String(noneOption.id) : undefined;
        const validAppliedIds = new Set(
            [...appliedIds]
                .map((id) => String(id))
                .filter(
                    (normalisedId) =>
                        normalisedId !== normalisedAllId &&
                        (!isNoneSpecial || normalisedId !== normalisedNoneId)
                )
        );

        const newPlaceholder = options
            .filter((option) => validAppliedIds.has(String(option.id)))
            .map((item) => item.name)
            .filter(Boolean)
            .join(', ');
        return newPlaceholder;
    });

    /** Count of applied options that appears in the trigger button (aka input) */
    public readonly appliedAndSelectedCount = computed(() => {
        const appliedIds = this.appliedAndSelectedOptionIds();
        let totalSelected = appliedIds.size;

        const allId = untracked(this.allOption)?.id;
        const noneId = untracked(this.noneOption)?.id;
        const isNoneSpecial = untracked(this.isNoneOptionSpecial);

        const hasAllOption =
            allId !== undefined ? appliedIds.has(allId) : false;
        const hasSpecialNoneOption =
            isNoneSpecial && noneId !== undefined
                ? appliedIds.has(noneId)
                : false;

        if (hasAllOption) {
            totalSelected = totalSelected - 1;
        }
        if (hasSpecialNoneOption) {
            totalSelected = totalSelected - 1;
        }

        return totalSelected;
    });

    /** Count of selected options that appears in the dropdown */
    protected readonly selectedCount = computed(() => {
        const selectedIds = this.selectedOptionIds();
        let totalSelected = selectedIds.size;

        const allId = untracked(this.allOption)?.id;
        const noneId = untracked(this.noneOption)?.id;
        const isNoneSpecial = untracked(this.isNoneOptionSpecial);

        const hasAllOption =
            allId !== undefined ? selectedIds.has(allId) : false;
        const hasSpecialNoneOption =
            isNoneSpecial && noneId !== undefined
                ? selectedIds.has(noneId)
                : false;

        if (hasAllOption) {
            totalSelected = totalSelected - 1;
        }
        if (hasSpecialNoneOption) {
            totalSelected = totalSelected - 1;
        }

        return totalSelected;
    });

    /**
     * Whether the filter is currently in compact/menu layout.
     * Returns false when used standalone (outside FilterGroup).
     */
    protected readonly isListLayout = computed(
        () => this.filterLayout?.layoutMode() === 'block'
    );
    // #endregion

    ngOnInit(): void {
        this.setupWithInitialAppliedOptions();
        this.forcePanelToMatchTriggerWidth();
    }

    // #region Template methods
    protected clearSearch() {
        this.search.set('');
    }

    protected isOptionSelected(id: U): boolean {
        return this.selectedOptionIds().has(id);
    }

    protected onToggleAll(checked: boolean): void {
        const { noneId, allItemIds, isNoneSpecial } =
            this.extractFilterSelectionData();

        if (checked) {
            this.selectAllOptions(allItemIds, isNoneSpecial, noneId);
        } else {
            this.deselectAllOptions({ noneId, isNoneSpecial });
        }
    }

    protected onToggleNone(checked: boolean): void {
        const { noneId, allItemIds, isNoneSpecial } =
            this.extractFilterSelectionData();

        if (noneId !== undefined && !isNoneSpecial) {
            this.onToggleOrdinary(noneId, checked);
            return;
        }

        if (checked) {
            this.deselectAllOptions({ noneId, isNoneSpecial });
        } else {
            this.selectAllOptions(allItemIds, isNoneSpecial, noneId);
        }
    }

    protected onToggleOrdinary(optionId: U, checked: boolean): void {
        if (this.allowSingleSelectionOnly()) {
            if (checked) {
                this.selectedOptionIds.set(new Set<U>().add(optionId));
            } else {
                this.selectedOptionIds.set(new Set<U>());
            }
        } else {
            const newSelectedIds = new Set(this.selectedOptionIds());

            if (checked) {
                newSelectedIds.add(optionId);
            } else {
                newSelectedIds.delete(optionId);
            }

            const {
                noneId,
                allItemIds,
                allNonSpecialItemIds,
                noSpecialValue,
                isNoneSpecial,
            } = this.extractFilterSelectionData();

            // Analyze current and previous selection state
            const {
                shouldSelectAll,
                shouldDeselectAll,
                shouldRemoveAllOrNoneOption,
            } = this.analyzeSelectionChange(
                newSelectedIds,
                allNonSpecialItemIds,
                noSpecialValue
            );

            // Apply appropriate selection action
            if (shouldSelectAll) {
                this.selectAllOptions(allItemIds, isNoneSpecial, noneId);
            } else if (shouldDeselectAll) {
                this.deselectAllOptions({ noneId, isNoneSpecial });
            } else if (shouldRemoveAllOrNoneOption) {
                this.updateSelectionWithoutSpecialOptions(
                    newSelectedIds,
                    noSpecialValue
                );
            } else {
                this.selectedOptionIds.set(new Set(newSelectedIds));
            }
        }
    }

    protected clearAll() {
        const { noneId, isNoneSpecial } = this.extractFilterSelectionData();
        this.deselectAllOptions({ noneId, isNoneSpecial });
        this.clearSearch();
    }

    /**
     * Handles the opening and closing events of the filter dropdown.
     * When closing without applying, restores the previously applied filter state.
     *
     * @param open - Boolean indicating if the dropdown is being opened (true) or closed (false)
     *
     * @remarks
     * - Acts as a cancellation mechanism when closing without applying changes
     * - Copies the last confirmed selection state from appliedAndSelectedFilterItems back to selectedFilterItems
     * - Only triggers restoration logic when the dropdown is closing (open === false)
     * ```
     */
    protected onDropdownClosed(): void {
        if (this.applyOnClose()) {
            this.applyFilters();
        } else {
            // set selected items as the applied and selected items
            const appliedItems = this.appliedAndSelectedOptionIds();
            this.selectedOptionIds.set(new Set(appliedItems));
        }

        this.clearSearch();
    }

    protected applyFiltersAndCloseDropdown() {
        this.applyFilters();
        this.menuTriggerRef()?.closeMenu();
    }

    protected compareItemsFn(item1: FilterOption<U>, item2: FilterOption<U>) {
        return item1 && item2 ? item1.id === item2.id : item1 === item2;
    }
    // #endregion

    // #region Private methods
    /**
     * Initializes filter selections based on `initialAppliedOptions` input.
     * Matches items to restore against the available filter options
     * and both applies and selects to matching items.
     *
     * @remarks
     * - Updates both current selection and applied filters states
     * - Early exits if no filters to restore or no filter items available
     */
    private setupWithInitialAppliedOptions() {
        this.initialAppliedOptions$
            .pipe(
                distinctUntilChanged(),
                /** Needed when wanting to clear selected options (inluding counter value)
                 *  after they've initally been applied */
                tap(() => {
                    if (!this.filter().options.length) {
                        this.selectedOptionIds.set(new Set());
                        this.appliedAndSelectedOptionIds.set(new Set());
                    }
                }),
                filter(
                    (restore) =>
                        (this.isNoneOptionSpecial() || !!restore) &&
                        (!!this.filter().options.length ||
                            this.noneOption() !== null)
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((filtersToRestore) => {
                // Build matching array including noneOption/allOption when they should be matchable
                const filterArray: FilterOption<U>[] = [
                    ...this.filter().options,
                ];
                const noneOpt = this.noneOption();
                const isNoneSpecialVal = this.isNoneOptionSpecial();

                // Include noneOption in matching when it's NOT special (treated as regular option)
                if (noneOpt !== null && !isNoneSpecialVal) {
                    filterArray.push(noneOpt as FilterOption<U>);
                }

                const itemsToApply = filterArray.filter((filterItem) => {
                    return (filtersToRestore ?? []).some((restoreItem) => {
                        if (!isPartialFilterOption(restoreItem)) {
                            // Match by name or id if restoreItem is primitive
                            const isNameString =
                                typeof restoreItem === 'string';
                            return (
                                (isNameString &&
                                    filterItem.name === restoreItem) ||
                                filterItem.id === restoreItem
                            );
                        } else if (
                            restoreItem.name === undefined &&
                            restoreItem.id === undefined
                        ) {
                            return false;
                        } else {
                            // Match by name and/or id if restoreItem is object
                            const nameMatch = restoreItem.name
                                ? filterItem.name === restoreItem.name
                                : true;
                            const idMatch =
                                restoreItem.id !== undefined
                                    ? filterItem.id === restoreItem.id
                                    : true;
                            return nameMatch && idMatch;
                        }
                    });
                });

                const appliedIds = new Set(
                    [...itemsToApply].map((item) => item.id)
                );

                // During initialization, directly set the matched options without
                // triggering "select all" or "deselect all" logic. This ensures we
                // restore exactly what was saved without side effects.
                if (appliedIds.size > 0) {
                    this.selectedOptionIds.set(appliedIds);
                } else {
                    // No matched options - check if we should select None (when special)
                    const { noneId, isNoneSpecial } =
                        this.extractFilterSelectionData();
                    this.deselectAllOptions({ noneId, isNoneSpecial });
                }

                // the above handles setting of selectedOptionIds
                // need to update the appliedAndSelectedIds to be the same
                this.appliedAndSelectedOptionIds.set(
                    new Set(this.selectedOptionIds())
                );
            });
    }

    private forcePanelToMatchTriggerWidth() {
        const menuTrigger = this.menuTriggerRef();
        menuTrigger?.menuOpened
            .pipe(
                filter(() => this.panelWidth() === 'match'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                const triggerElement = this.menuTriggerEl()?.nativeElement;
                if (!(triggerElement instanceof HTMLButtonElement)) return;

                const width = `${triggerElement.offsetWidth}px`;
                const panelId = menuTrigger.menu?.panelId;
                const selector = panelId
                    ? `#${panelId}.filter-menu`
                    : '.filter-menu';

                const menuPanel = document.querySelector(selector);

                if (menuPanel instanceof HTMLElement) {
                    menuPanel.style.setProperty(
                        '--filter-trigger-width',
                        width
                    );
                }
            });
    }

    private extractFilterSelectionData() {
        const allId = this.allOption()?.id;
        const noneId = this.noneOption()?.id;
        const allItemIds = [...this.filter().options].map(
            (option) => option.id
        );

        if (allId !== undefined) {
            allItemIds.push(allId);
        }

        if (noneId !== undefined) {
            allItemIds.push(noneId);
        }

        const isNoneSpecial = this.isNoneOptionSpecial();

        const noSpecialValue = (id: U) => {
            // Normalize IDs for comparison to handle string vs number mismatches
            const normalizedId = String(id);
            const normalizedAllId =
                allId !== undefined ? String(allId) : undefined;
            const normalizedNoneId =
                noneId !== undefined ? String(noneId) : undefined;
            return (
                normalizedId !== normalizedAllId &&
                (!isNoneSpecial || normalizedId !== normalizedNoneId)
            );
        };

        const allNonSpecialItemIds = allItemIds.filter(noSpecialValue);

        return {
            allItemIds,
            allNonSpecialItemIds,
            allId,
            noneId,
            noSpecialValue,
            isNoneSpecial,
        };
    }

    private analyzeSelectionChange(
        rawSet: Set<U>,
        allNonSpecialItems: U[],
        noSpecialValue: (id: U) => boolean
    ) {
        // Extract IDs from newly selected values
        const newValueIds = [...rawSet];
        const prevValueIds = [...this.selectedOptionIds()];

        // Filter out special values option for specific checks
        const newNonSpecialValueIds = new Set(
            [...newValueIds].filter(noSpecialValue)
        );
        const prevNonSpecialValueIds = new Set(
            [...prevValueIds].filter(noSpecialValue)
        );

        const hasOptionsSelected = !!newNonSpecialValueIds.size;

        const toggleAllOnByOtherOptions =
            hasOptionsSelected &&
            newNonSpecialValueIds.size === allNonSpecialItems.length &&
            prevNonSpecialValueIds.size !== allNonSpecialItems.length;

        const toggleAllOffByOtherOptions =
            newNonSpecialValueIds.size !== allNonSpecialItems.length &&
            prevNonSpecialValueIds.size === allNonSpecialItems.length;

        const toggleNoneOnByOtherOptions = !hasOptionsSelected;

        const toggleNoneOffByOtherOptions =
            hasOptionsSelected &&
            newNonSpecialValueIds.size === 1 &&
            prevNonSpecialValueIds.size === 0;

        return {
            shouldSelectAll: toggleAllOnByOtherOptions,
            shouldDeselectAll: toggleNoneOnByOtherOptions,
            shouldRemoveAllOrNoneOption:
                toggleAllOffByOtherOptions || toggleNoneOffByOtherOptions,
        };
    }

    private selectAllOptions(
        allItemIds: U[],
        isNoneSpecial: boolean,
        noneId: U | undefined
    ) {
        const noSpecialNoneValue = (id: U) => !isNoneSpecial || id !== noneId;
        const allItemsWithoutSpecialNoneIds =
            allItemIds.filter(noSpecialNoneValue);
        this.selectedOptionIds.set(new Set(allItemsWithoutSpecialNoneIds));
    }

    /** special options includes ALL and maybe NONE */
    private updateSelectionWithoutSpecialOptions(
        rawSet: Set<U>,
        noSpecialValue: (id: U) => boolean
    ) {
        const selectedIds = [...rawSet].filter(noSpecialValue);
        this.selectedOptionIds.set(new Set(selectedIds));
    }

    private deselectAllOptions({
        noneId,
        isNoneSpecial,
    }: {
        noneId: U | undefined;
        isNoneSpecial: boolean;
    }): void {
        // if there is a "None" option AND it's treated as special, then select it
        if (noneId !== undefined && isNoneSpecial) {
            this.selectedOptionIds.set(new Set([noneId]));
        } else {
            // Otherwise, just clear the selection
            this.selectedOptionIds.set(new Set());
        }
    }

    /**
     * Applies the current filter selection state and emits the selected items.
     * Creates a new copy of selections to prevent mutation issues.
     *
     * @emits appliedChange - Emits array of selected filter item ids
     *
     * @remarks
     * - Updates appliedAndSelectedFilterItems with current selections
     * - Creates new array references using spread operator to prevent mutations
     * - Emits selected items through appliedChange output
     * - Automatically closes the dropdown after applying
     */
    private applyFilters(): void {
        const itemsToBeApplied = [...this.selectedOptionIds()];
        this.appliedAndSelectedOptionIds.set(new Set(itemsToBeApplied));

        const returnAllOption = (id: U) =>
            this.returnAllOption() ? true : id !== this.allOption()?.id;
        const returnNoneOption = (id: U) =>
            !this.isNoneOptionSpecial() || this.returnNoneOption()
                ? true
                : id !== this.noneOption()?.id;

        const appliedIds: AppliedFilterOptionId<U>[] = itemsToBeApplied.filter(
            (id) => returnAllOption(id) && returnNoneOption(id)
        );
        this.appliedChange.emit(appliedIds);
    }
    // #endregion
}
